import React, { useEffect, useState } from 'react';
import { Button, Label, Progress, UncontrolledTooltip } from 'reactstrap';
import { Image, Mic, Paperclip, Send } from 'react-feather';
import { useDispatch, useSelector } from 'react-redux';
import useI18n from 'hooks/use-i18n';
import { handleToastError } from 'helpers';
import chatApi from 'api/chatApi';
import { setMessage } from 'store/messages';
import styles from 'styles/Chat.module.scss';
import { IconButton, Row, TextComposer, TextInput } from '@livechat/ui-kit';
import { showToastError } from '@core/components/toastify/ToastTypes';
import { useSelectedGroup } from 'hooks/messages';
import dynamic from 'next/dynamic';
import UploadFileDetail from './UploadFileDetail';
import { useSocket } from '../../../../hooks/useSocket';
import { SOCKET_NAMESPACE } from '../../../../constants/constants';
import { getColor, getColorText } from 'store/settings';
import styled from '@emotion/styled';
import { LIMIT_CHAT_FILE_UPLOAD, ONEKILOBYTE } from 'constants/constants';
import bytes from 'bytes';

export const allowTypeDocuments = [
  // images
  'image/jpeg',
  'image/pjpeg',
  'image/png',
  'image/gif',
  'image/svg+xml',
  // video
  'video/avi',
  'application/x-troff-msvideo',
  'video/msvideo',
  'video/x-msvideo',
  'audio/x-wav',
  'video/quicktime',
  'video/x-quicktime',
  'image/mov',
  'audio/aiff',
  'audio/x-midi',
  'video/mp4',
  'video/m4a',
  'application/mp4',
  'audio/ogg',
  'video/ogg',
  'video/x-ms-wmv',
  'video/x-ms-asf',
  'video/webm',
  'audio/webm',
  // powerpoint
  'application/pdf',
  'application/powerpoint',
  'application/mspowerpoint',
  'application/vnd.ms-powerpoint',
  'application/x-mspowerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.slideshow',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  // word
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
  'application/doc',
  'application/ms-doc',
  // excel
  'application/vnd.ms-excel',
  'application/excel',
  'application/x-excel',
  'application/x-msexcel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  // zip
  'application/zip',
  'application/x-zip',
  'application/x-zip-compressed',
  'application/octet-stream',
];

export const acceptTypeImages = [
  'image/jpeg',
  'image/pjpeg',
  'image/png',
  'image/gif',
  'image/svg+xml',
];

const RecordAudioBoxNoSSR = dynamic(() => import('./RecordAudioBox'), {
  ssr: false,
});

const InputChat = (props) => {
  const { scrollToBottom } = props;
  const i18n = useI18n();
  const dispatch = useDispatch();
  const selectedGroup = useSelectedGroup();
  const metaData = useSelector((state) => state.settingAccount.metaData);
  useSocket(SOCKET_NAMESPACE.GROUP_CHAT, { group: selectedGroup?._id });

  const [files, setFiles] = useState(undefined);
  const [text, setText] = useState('');
  const [isBusy, setBusy] = useState(false);
  const [typeFile, setTypeFile] = useState('image');
  const [startRecord, setStartRecord] = useState(false);
  const [progress, setProgress] = useState(null);
  //Color Theme
  const colorStatus = useSelector(getColor);
  const colorTextStatus = useSelector(getColorText);
  const Stylescal = styled.div`
    .btn-danger {
      border-color: ${colorStatus} !important;
      background-color: ${colorStatus} !important;
      color: ${colorTextStatus} !important;
    }
    .btn-danger:hover {
      background-color: #ffffff !important;
      color: ${colorStatus} !important;
      border-color: ${colorStatus} !important;
    }
  `;
  function resetInput() {
    setBusy(false);
    setFiles(undefined);
    setText('');
    document.getElementById('input-message').value = '';
    document.getElementById('input-message').innerHTML = '';
    document.getElementById('input-message').focus();
  }

  async function onSendMessage() {
    try {
      if (text?.trim() || files?.length) {
        const formData = new FormData();
        if (files?.length) {
          files.forEach((item) => {
            formData.append('message-files', item);
          });
        }
        const data = {
          content: {
            text: text.trim(),
          },
        };
        formData.append('data', JSON.stringify(data));
        if (selectedGroup?._id && (text || files?.length)) {
          setProgress(true);
          const config = {
            onUploadProgress: (progressEvent) => {
              const percentCompleted = Math.round(
                (progressEvent?.loaded * 100) / progressEvent?.total,
              );
              const progressUpload = document.getElementById(
                'progress-upload-input-chat',
              ).firstChild;
              if (progressUpload) {
                progressUpload.style.width = `${percentCompleted}%`;
              }
            },
          };
          setBusy(true);
          setFiles(undefined);
          const response = await chatApi.createMessage(
            selectedGroup._id,
            formData,
            config,
          );
          setProgress(false);
          await dispatch(setMessage(response));
          resetInput();
          setText('');
          scrollToBottom();
        }
      }
    } catch (error) {
      handleToastError(error, i18n);
    }
  }

  async function onSendAudio(file) {
    try {
      if (
        file?.size >
        (metaData?.chatMaxSize
          ? bytes.parse(ONEKILOBYTE * metaData?.chatMaxSize)
          : LIMIT_CHAT_FILE_UPLOAD)
      ) {
        showToastError(
          `Upload file size is exceeds the file size. Limit is ${
            metaData?.chatMaxSize ? metaData?.chatMaxSize : 25
          }MB.`,
        );
        return;
      }
      const formData = new FormData();
      formData.append('message-files', file);
      if (selectedGroup?._id) {
        setProgress(true);
        const config = {
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent?.loaded * 100) / progressEvent?.total,
            );
            const progressUpload = document.getElementById(
              'progress-upload-input-chat',
            ).firstChild;
            if (progressUpload) {
              progressUpload.style.width = `${percentCompleted}%`;
            }
          },
        };
        setBusy(true);
        const response = await chatApi.createMessage(
          selectedGroup._id,
          formData,
          config,
        );
        setProgress(false);
        await dispatch(setMessage(response));
        scrollToBottom();
        setBusy(false);
        document.getElementById('input-message').focus();
      }
    } catch (error) {
      setBusy(false);
      handleToastError(error, i18n);
    }
  }

  function onKeyDown(event) {
    if (!event?.shiftKey && event?.keyCode === 13) {
      onSendMessage();
    }
  }

  function onChange(e, type = 'image') {
    if (e.target.files?.length) {
      setTypeFile(type);
      const acceptTypes =
        type === 'image' ? acceptTypeImages : allowTypeDocuments;
      const index = Object.values(e.target.files).findIndex(
        (item) =>
          item?.size >
          (metaData?.chatMaxSize
            ? bytes.parse(ONEKILOBYTE * metaData?.chatMaxSize)
            : LIMIT_CHAT_FILE_UPLOAD),
      );
      const acceptFiles = Object.values(e.target.files)?.filter(
        (item) =>
          item?.type &&
          acceptTypes.indexOf(item?.type) !== -1 &&
          item?.size / (1024 * 1024) <= 25,
      );
      if (index !== -1) {
        showToastError(
          `Upload file size is exceeds the file size. Limit is ${
            metaData?.chatMaxSize ? metaData?.chatMaxSize : 25
          }MB.`,
        );
        return;
      }
      if (!acceptFiles?.length) {
        showToastError(`No any ${type} available!`);
        return;
      }
      if (acceptFiles?.length > 15) {
        showToastError('Upload files is exceeds the files. Limit is 15.');
        return;
      }
      setFiles(acceptFiles);
      document.getElementById('input-message')?.focus();
    }
  }

  function onChangeForm(event) {
    setText(event.target.value);
  }

  return (
    <>
      {progress ? (
        <Progress
          striped
          id="progress-upload-input-chat"
          className={styles['progress-upload-message']}
        />
      ) : null}

      <TextComposer
        active
        onKeyDown={onKeyDown}
        onChange={onChangeForm}
        value={text}
        className={styles['wrap-input-toolbar']}
      >
        <Row align="left">
          {!startRecord && (
            <UploadFileDetail
              files={files}
              isBusy={isBusy}
              setFiles={setFiles}
              typeFile={typeFile}
            />
          )}
          <RecordAudioBoxNoSSR
            start={startRecord}
            setStartRecord={setStartRecord}
            uploadAudio={onSendAudio}
          />
        </Row>
        <Row align="center">
          <IconButton
            id="icon-record"
            fit
            className={styles['input-group__icon']}
            onClick={() => setStartRecord(!startRecord)}
          >
            <Mic className="cursor-pointer" size={16} />
            <UncontrolledTooltip placement="top" target="icon-record">
              Record
            </UncontrolledTooltip>
          </IconButton>

          <IconButton fit className={styles['input-group__icon']}>
            <Label className="attachment-icon mb-0" for="attach-file">
              <Paperclip
                className="cursor-pointer text-secondary"
                size={16}
                id="label-attach-file"
              />
              <UncontrolledTooltip placement="top" target="label-attach-file">
                Attach files
              </UncontrolledTooltip>
              {!isBusy && (
                <input
                  type="file"
                  id="attach-file"
                  hidden
                  disabled={isBusy}
                  accept={allowTypeDocuments}
                  onChange={(e) => onChange(e, 'file')}
                  onClick={(event) => {
                    event.target.value = null;
                  }}
                  multiple
                />
              )}
            </Label>
          </IconButton>
          <IconButton
            fit
            className={styles['input-group__icon']}
            disabled={isBusy}
          >
            <Label
              className="attachment-icon mb-0"
              for="attach-doc"
              id="label-attach-image"
            >
              <Image className="cursor-pointer text-secondary" size={16} />
              {!isBusy && (
                <input
                  type="file"
                  id="attach-doc"
                  hidden
                  multiple
                  accept={[
                    'image/jpeg',
                    'image/pjpeg',
                    'image/gif',
                    'image/png',
                    'image/svg+xml',
                  ]}
                  disabled={isBusy}
                  onChange={(e) => onChange(e, 'image')}
                  onClick={(event) => {
                    event.target.value = null;
                  }}
                />
              )}

              <UncontrolledTooltip placement="top" target="label-attach-image">
                Attach images
              </UncontrolledTooltip>
            </Label>
          </IconButton>
          <div className={styles['wrap-input-group']}>
            <TextInput
              id="input-message"
              autoFocus
              value={text}
              disabled={isBusy}
              className={`${styles['input-text-chat']} custom-scrollbar-scroll ml-05`}
              placeholder="Type your message"
            />
          </div>
          <div className="flexRow align-items-center">
            <Stylescal>
              <Button
                className="send round"
                color="danger"
                disabled={(!text && !files?.length) || isBusy}
                onClick={onSendMessage}
              >
                <Send size={14} className="d-lg-none" />
                <span className="d-none d-lg-block">Send</span>
              </Button>
            </Stylescal>
          </div>
        </Row>
      </TextComposer>
    </>
  );
};

export default InputChat;
